import { useTranslation } from 'react-i18next';
import { trDisplay, trTemplate } from '../../i18n';
import React, { useEffect, useState } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { SWRHelp } from './SWRHelp';
import { buildSWRScenarioSnapshot, saveSWRScenario } from '../../lib/swrScenarioStore';
import { buildSWRScenarioName } from './swrScenarioName';

const SWR_SCENARIOS_CHANGED_EVENT = 'swr-scenarios-changed';

const SWRScenarioSavePanel = ({ results, scenarioParams, hasRun, onScenarioSaved }) => {
  useTranslation();
  const [scenarioName, setScenarioName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState('');

  // Only a new completed run proposes a new name. Card/stress patches and
  // language changes must preserve the user's edit and the result association.
  useEffect(() => {
    if (hasRun && scenarioParams) {
      setScenarioName(buildSWRScenarioName(scenarioParams));
      setStatus('');
    }
  }, [hasRun, scenarioParams]);

  const handleSave = async () => {
    if (!hasRun || !results || !scenarioParams) return;
    setIsSaving(true);
    try {
      const snapshot = buildSWRScenarioSnapshot({
        name: scenarioName,
        scenarioParams,
        results,
      });
      await saveSWRScenario(snapshot);
      setStatus({ source: 'Scenario "{{v0}}" salvato per il confronto.', values: { v0: snapshot.name } });
      onScenarioSaved?.(snapshot);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(SWR_SCENARIOS_CHANGED_EVENT, { detail: { scenarioId: snapshot.id } }));
      }
    } catch (error) {
      console.error('Errore salvataggio scenario SWR:', error);
      setStatus('Non riesco a salvare lo scenario nel browser.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-blue-100 bg-white p-3 sm:p-4 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-700" htmlFor="swr-save-scenario-name">{trDisplay("Salva scenario per confronto")}</label>
            <SWRHelp topic="saveScenario" side="bottom" />
          </div>
          <Input
            id="swr-save-scenario-name"
            value={scenarioName}
            onChange={(event) => setScenarioName(event.target.value)}
            placeholder={trDisplay("es. Scenario base con buffer")}
            disabled={!hasRun || isSaving}
            className="mt-2 bg-white"
          />
        </div>
        <Button
          type="button"
          onClick={handleSave}
          disabled={!hasRun || !results || isSaving}
          className="bg-blue-600 hover:bg-blue-700 text-white min-h-[40px]"
          data-testid="swr-save-current-scenario"
        >
          {trDisplay(isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />)}{trDisplay("Salva scenario")}</Button>
      </div>
      {trDisplay(!hasRun && (
        <p className="text-xs text-slate-500 mt-2">{trDisplay("Esegui una simulazione per salvare lo scenario corrente.")}</p>
      ))}
      {status && <p className="text-xs text-blue-700 mt-2">{status.source ? trTemplate(status.source, status.values) : trDisplay(status)}</p>}
    </div>
  );
};

export { SWR_SCENARIOS_CHANGED_EVENT };
export default SWRScenarioSavePanel;
